<template>

  <v-container fluid class="px-0 pt-0 shipper-item-edit-licenses">

    <!-- (data loading) -->
    <template v-if="isDataLoading || isSaving">
      <v-row>
        <v-col class="mt-4 d-flex align-center justify-center" style="height:120px">
          <v-progress-circular indeterminate :size="50" color="primary" />
        </v-col>
      </v-row>
    </template>

    <template v-else-if="shipperToEdit">
      <!---------------->
      <!-- Breadcrumb -->
      <!---------------->
      <v-row class="mt-2 mb-6">
        <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
          <!-- (breadcrumb) -->
          <v-icon small class="mr-2">mdi-link</v-icon>
          <router-link :to="{ name: 'provider-connection-list' }">
            Shipper Accounts
          </router-link>
          <div class="separator">/</div>
          <div class="active-item"><span class="label-prefix">Edit Location Licenses:</span> {{ shipperToEdit.name }}</div>
        </v-col>
      </v-row>

      <!--------------------------------->
      <!-- Page Actions (cancel, save) -->
      <!--------------------------------->
      <v-toolbar flat class="item-controls item-editor-controls mb-4">
        <v-btn color="gray" text-color="black" @click="cancel">
          <span v-if="isDirty">Cancel</span>
          <span v-else>Close</span>
        </v-btn>
        <!-- <template v-if="!isValidLicenseNo">
          <v-spacer />
          <v-btn color="yellow darken-3" outlined>Invalid license number</v-btn>
        </template> -->
        <v-spacer />
        <dialog-confirm-button
            v-if="isDirty"
            :buttonWidth="160"
            :buttonHeight="43"
            buttonText="Save Changes"
            buttonColor="primary"
            :message="confirmSaveMessage"
            @confirmed="save" />
      </v-toolbar>

      <!-- Page Title -->
      <v-row class="pa-0 ma-0">
        <v-col cols="12" xs="12" sm="12" md="9" lg="6" class="pa-0 ma-0">
          <div class="page-title">
            Edit Licenses for {{ shipperToEdit.name }}
          </div>
        </v-col>
      </v-row>

      <!-------------------------------->
      <!-- Edit Establishment License -->
      <!-------------------------------->
      <v-container fluid class="mt-6 page-content">
        <!-- Title -->
        <v-row class="pa-0 ma-0">
          <div class="title">Marijuana Establishment License (Adult-Use)</div>
        </v-row>

        <!-- (contains shipping address selector) -->
        <v-row class="pa-0 ma-0 mt-4">
          <v-checkbox
            class="ml-2"
            hide-details
            label="Contains shipping address"
            v-model="isAddressForEstablishmentLicense"
            @click="handleSelectionOfAddress('establishment_license')" />

          <!-- (note on approval requirements) -->
          <!-- <v-tooltip right v-if="isAddressForEstablishmentLicense">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="yellow darken-3"
                  style="cursor:pointer;margin-left:8px;margin-top:20px"
                  v-bind="attrs"
                  v-on="on">
                mdi-alert
              </v-icon>
            </template>
            <div>{{ tooltipTextForRequiredFields }}</div>
          </v-tooltip> -->
        </v-row>

        <!-- (license manager) -->
        <v-row class="pa-0 ma-0 mt-8 mr-6 ml-2">
          <license-manager
              ref="establishment-license-manager"
              licenseType="establishment_license"
              :item="activeShipper"
              :itemToEdit="shipperToEdit"
              :showAddressFields="isAddressForEstablishmentLicense"
              fileDisplayWidth="100%"
              allowedFileTypes="$root.allowedFileTypesForUploads"
              @dirty="setDirty" />
        </v-row>
      </v-container>

      <!---------------------------------------------->
      <!-- Edit Dispensary Registration Certificate -->
      <!---------------------------------------------->
      <v-container fluid class="mt-8 page-content">
        <!-- Title -->
        <v-row class="pa-0 ma-0">
          <div class="title">Dispensary Registration Certificate (Medical)</div>
        </v-row>

        <!-- (contains shipping address selector) -->
        <v-row class="pa-0 ma-0 mt-4">
          <v-checkbox
            class="ml-2"
            hide-details
            label="Contains shipping address"
            v-model="isAddressForDrc"
            @click="handleSelectionOfAddress('drc')" />

          <!-- (note on approval requirements) -->
          <!-- <v-tooltip right v-if="isAddressForDrc">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="yellow darken-3"
                  style="cursor:pointer;margin-left:8px;margin-top:20px"
                  v-bind="attrs"
                  v-on="on">
                mdi-alert
              </v-icon>
            </template>
            <div>{{ tooltipTextForRequiredFields }}</div>
          </v-tooltip> -->
        </v-row>

        <!-- (license manager) -->
        <v-row class="pa-0 ma-0 mt-8 mr-6 ml-2">
          <license-manager
              ref="drc-manager"
              licenseType="drc"
              :item="activeShipper"
              :itemToEdit="shipperToEdit"
              :showAddressFields="isAddressForDrc"
              fileDisplayWidth="100%"
              allowedFileTypes="$root.allowedFileTypesForUploads"
              @dirty="setDirty" />
        </v-row>
      </v-container>
    </template>

  </v-container>

</template>

<script>
import LicenseManager from '@/components/app/LicenseManager'
import DialogConfirmButton from '@/components/app/DialogConfirmButton'

export default {
  name: 'ShipperItemEditLicensesPage',

  components: {
    LicenseManager,
    DialogConfirmButton
  },

  props: {
    shipperId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isDataLoading: false,
      isSaving: false,
      savingError: null,
      isDirty: false,
      activeShipper: null,
      // ---------- License management
      isAddressForEstablishmentLicense: true,
      isAddressForDrc: false,
      licenseDocsToUpload: null,
      licenseDocsMeta: null
    }
  },

  computed: {
    uiType () {
      return this.$root.uiType()
    },
    // The active item being edited
    shipperToEdit () {
      return this.$store.getters['sh/itemEditing']('shipper')
    },
    tooltipTextForRequiredFields () {
      return 'All fields are required for the location to be approved.'
    },
    isValidLicenseNo () {
      if (!this.shipperToEdit) {
        return true
      }
      const estLicenseNumber = this.shipperToEdit.establishment_license_number
      const drcLicenseNumber = this.shipperToEdit.drc_number
      return (!estLicenseNumber || !!(estLicenseNumber && estLicenseNumber.length === 20)) &&
             (!drcLicenseNumber || !!(drcLicenseNumber && drcLicenseNumber.length === 20))
    },
    confirmSaveMessage () {
      if (this.uiType === 'sys-admin') return 'Save license changes for this location?'
      else return 'Saving changes to your license information will require review from RouteLeaf'
    }
  },

  async beforeMount () {
    // Ensure shipperToEdit is loaded
    if (!this.shipperToEdit) {
      this.isDataLoading = true

      this.activeShipper = await this.$store.dispatch('sh/dataApi', {
        uri: `shipper/${this.shipperId}`,
        opts: {
          params: { with: 'recipient_emails' }
        }
      })
      this.$store.dispatch('sh/setItemToEdit', this.activeShipper)

      this.isDataLoading = false
    } else {
      // Make a copy of the original data
      this.activeShipper = this.$root.utils.cloneDeep(this.shipperToEdit)
    }

    // Set shipping address selection (flip default checkbox selection)
    if (this.shipperToEdit.license_for_shipping_address === 'drc') {
      this.isAddressForEstablishmentLicense = false
      this.isAddressForDrc = true
    }
  },

  beforeDestroy () {
    // Clear editing registry
    this.$store.dispatch('sh/clearItemEditing', 'shipper')
  },

  methods: {
    setDirty (dirtyProps) {
      if (dirtyProps && dirtyProps.length > 0) this.isDirty = true
      else this.isDirty = false
    },

    handleSelectionOfAddress (license) {
      // Flip checkboxes
      if (license === 'drc') this.isAddressForEstablishmentLicense = !this.isAddressForEstablishmentLicense
      if (license === 'establishment_license') this.isAddressForDrc = !this.isAddressForDrc
    },

    cancel () {
      this.isDirty = false
      // Return to loc item page
      this.$root.goTo('shipper-item', { shipperId: this.shipperId })
    },

    async uploadLicenseDocs (shipper, docs) {
      if (!shipper || !docs) return false

      for (const doc of docs) {
        const assetKey = doc.asset_key
        const file = doc.file

        if (assetKey && file) {
          let uploadInfo = null
          try {
            // Generate upload URL
            uploadInfo = await this.$store.dispatch('sh/dataApi', {
              method: 'get',
              uri: `location/${shipper.id}/asset/uploadUrl`,
              to_model: false,
              opts: {
                params: { asset_key: assetKey }
              }
            })

            // Upload document
            await fetch(uploadInfo.url, { method: 'PUT', body: file })

            // Set uploaded flag
            if (assetKey === 'establishment_license') shipper.saved_establishment_license = true
            if (assetKey === 'drc') shipper.saved_drc = true
          } catch (err) {
            console.log(`Error uploading license document: ${assetKey} for shipper ${shipper.id} =>`, err)
          }
        }
      }
    },

    async save () {
      // check for valid license number
      // if (!this.isValidLicenseNo) { return }

      // Check for license document updates
      const licenseDocs = []
      const estLicenseManager = this.$refs['establishment-license-manager']
      const estLicenseDoc = estLicenseManager.getUpdatedLicenseDoc()
      if (estLicenseDoc) licenseDocs.push(estLicenseDoc)

      const drcManager = this.$refs['drc-manager']
      const drcLicenseDoc = drcManager.getUpdatedLicenseDoc()
      if (drcLicenseDoc) licenseDocs.push(drcLicenseDoc)

      // Set license type that represents the shipping address
      const licenseForAddress = (this.isAddressForDrc) ? 'drc' : 'establishment_license'
      this.shipperToEdit.license_for_shipping_address = licenseForAddress

      // Set docs to upload, build meta package to add to shipper
      if (licenseDocs.length > 0) {
        this.licenseDocsToUpload = licenseDocs // set license docs to upload
        const docInfo = {}
        for (const doc of licenseDocs) {
          if (doc.asset_key && doc.file) {
            docInfo[doc.asset_key] = {
              label: doc.label,
              category: doc.asset_key,
              file_name: doc.file_name,
              file_type: doc.file_type,
              file_extension: doc.file_extension
            }
          }
        }
        this.licenseDocsMeta = docInfo
      }

      // Perform save if forms are valid
      const estLicenseFormValid = (estLicenseManager.showForm) ? await estLicenseManager.validate() : true
      const drcFormValid = (drcManager.showForm) ? await drcManager.validate() : true
      if (estLicenseFormValid && drcFormValid) this.saveEdit()
    },

    // In the future, we will stay here in the edit page. Requires usage of the isDirty pattern - |M|
    async saveEdit () {
      this.isSaving = true

      // Upload license docs
      if (this.licenseDocsToUpload) {
        // TODO - We should wrap the try/catch here !!!
        await this.uploadLicenseDocs(this.shipperToEdit, this.licenseDocsToUpload)
        this.shipperToEdit.license_docs = this.licenseDocsMeta // attach meta
        this.shipperToEdit.licenses_changed = this.licenseDocsToUpload // include file changes
      }

      // Update shipper
      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `shipper/${this.shipperId}`,
          opts: {
            bodyData: this.shipperToEdit
          }
        })

        // Return to item page
        this.$root.goTo('shipper-item', { shipperId: this.shipperId })
      } catch (err) {
        console.error('[DEVING] Error saving shipper =>', err)
        this.savingError = err.message || err.text || err
        this.isSaving = false
      }
    }
  }
}
</script>

<style>
  .shipper-item-edit-licenses .v-window__container {
    max-width: 960px !important;
  }
</style>
